import { useEffect, useState, useRef } from 'react';
import { Popover } from '@headlessui/react';
import { usePopper } from 'react-popper';
import { useTranslation } from '@/helpers/translate';
import dayjs from '@/helpers/dayjs';
import Icon from '@/core/icon';
import {
  CheckIcon, menuClass, menuItemClass, menuDividerClass, menuDangerClass,
} from './lightUi';

/*
`light` : variante claire (homepage / popin) — menu blanc compact ancré sous le jour,
avec des classes explicites plutôt que des surcharges CSS de la version sombre.
`labels` : libellés des périodes (journée / matin / après-midi / heure / retirer) ;
à défaut on prend les clés i18n `btn:period.*`.
*/
function PeriodPopover({
  item, onClose, store, light, labels,
}) {
  const divRef = useRef();
  const { t } = useTranslation();
  const dateCurrent = dayjs(item.date);

  const [refLabel, setRefLabel] = useState();
  const [refPopover, setRefPopover] = useState();
  const { attributes } = usePopper(refLabel, refPopover, { placement: 'bottom' });

  useEffect(() => {
    function handleClickOutside(event) {
      if (divRef && divRef.current && !divRef.current.contains(event.target)) {
        onClose();
      }
    }
    window.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  function onClickPeriodSelect(_period) {
    if (dateCurrent) {
      const period = store.getPeriod();
      const current = store.getDatesPicked();
      if (_period === 'delete') {
        const index = current.indexOf(dateCurrent.formatAsKey());
        delete period[dateCurrent.formatAsKey()];
        current.splice(index, 1);
        // state.selected = false
      } else {
        // state.selected = true
        // Une date porte SOIT une période SOIT un créneau : choisir une période retire le créneau.
        store.removeSlotForDate?.(dateCurrent.formatAsKey());
        period[dateCurrent.formatAsKey()] = _period;
        if (current.indexOf(dateCurrent.formatAsKey()) === -1) {
          current.push(dateCurrent.formatAsKey());
        }
      }

      current.sort((d) => d);
      const df = Object.keys(period);
      df.sort((d) => dayjs(d));
      const ordered = df.reduce((acc, d) => {
        acc[d] = period[d];
        return acc;
      }, {});
      store.setDatesPicked(df);
      store.setPeriod(ordered);
    }
    onClose();
  }

  const label = (key) => labels?.[key] || t(`btn:period.${key}`);

  // WEA-163 — la période "soirée" (night) est retirée du front.
  const periods = [
    { text: 'day', value: 0 },
    { text: 'morning', value: 1 },
    { text: 'afternoon', value: 2 },
  ];

  /*
    WEA-163 — « Par heure » sélectionne la date ET la passe en horaire d'un seul geste, avec le
    créneau par défaut. La date est enregistrée en période 0 : c'est le créneau qui la marque
    comme horaire, la période ne sert que de repli si le client revient en journée.
  */
  const isHourly = !!store.slotOf?.(dateCurrent.formatAsKey());
  const onClickHourly = () => {
    onClickPeriodSelect(0);
    store.setSlotForDate(dateCurrent.formatAsKey(), { start: '09:00', end: '10:00' });
  };

  const isDeletable = store.getDatesPicked().indexOf(dateCurrent.formatAsKey()) !== -1;
  const isPeriodSelected = (value) => !isHourly && store.getPeriod()[dateCurrent.formatAsKey()] === value;

  const renderDark = () => (
    <div className="relative text-base text-white border rounded-lg bg-purple-6 shadow-button border-purple/50" ref={divRef}>
      {periods.map((p) => {
        const isSelected = store.getPeriod()[dateCurrent.formatAsKey()] === p.value;
        return (
          <button
            key={p.text}
            onClick={() => { onClickPeriodSelect(p.value); }}
            disabled={isSelected}
            className={`w-32 p-2  ${isSelected ? 'bg-purple-6 text-purple cursor-default opacity-100' : 'cursor-pointer hover:text-white hover:bg-purple-5'}`}
          >
            {t(`btn:period.${p.text}`)}
          </button>
        );
      })}
      <button
        onClick={onClickHourly}
        disabled={isHourly}
        className={`w-32 p-2 border-t border-purple/40 ${isHourly ? 'bg-purple-6 text-purple cursor-default opacity-100' : 'cursor-pointer hover:text-white hover:bg-purple-5'}`}
      >
        {t('btn:period.hour')}
      </button>
      {!isDeletable || <button className="flex justify-center w-full px-3 py-3 cursor-pointer group hover:text-red hover:bg-red/20" onClick={() => { onClickPeriodSelect('delete'); }}><Icon className="w-4 h-4 text-red group-hover:text-red " name="trash" /></button>}
    </div>
  );

  const renderLight = () => (
    <div className={menuClass} ref={divRef}>
      {periods.map((p) => {
        const selected = isPeriodSelected(p.value);
        return (
          <button
            key={p.text}
            type="button"
            onClick={() => { onClickPeriodSelect(p.value); }}
            disabled={selected}
            className={menuItemClass(selected)}
          >
            {label(p.text)}
            {selected && <CheckIcon />}
          </button>
        );
      })}
      <div className={menuDividerClass} />
      <button
        type="button"
        onClick={onClickHourly}
        disabled={isHourly}
        className={menuItemClass(isHourly)}
      >
        {label('hour')}
        {isHourly && <CheckIcon />}
      </button>
      {isDeletable && (
        <>
          <div className={menuDividerClass} />
          <button
            type="button"
            className={menuDangerClass}
            onClick={() => { onClickPeriodSelect('delete'); }}
          >
            {label('remove')}
          </button>
        </>
      )}
    </div>
  );

  return (
    <Popover>
      <span ref={setRefLabel} />
      <Popover.Panel
        className={`absolute z-10 block transform -translate-x-1/2 left-1/2 ${light ? 'mt-1.5' : 'mt-1'}`}
        static
        ref={setRefPopover}
        {...attributes.popper}
      >
        {light ? renderLight() : renderDark()}
      </Popover.Panel>
    </Popover>
  );
}

export default PeriodPopover;
